/**
 * App.tsx - Main Application Entry Point
 *
 * Root component that sets up the application routing and context providers.
 * Handles circle-based routing with proper data loading and provider composition.
 */

import { CircleProvider, useCircleContext } from '@/contexts/CircleContext'
import { AuthProvider } from '@/contexts/AuthContext'
import { ChatProvider } from '@/contexts/ChatContext'
import { AssistantsProvider } from '@/contexts/AssistantsContext'
import { FilterProvider } from '@/contexts/FilterContext'
import './App.css'
import { HashRouter, Route, Routes, useParams } from "react-router-dom"

import { Suspense } from 'react'
import {
    APIProvider,
    API,
    isLocalhost,
    useProfile,
    LoadCircleData,
    useApi,
    LanguageSelectionContextProvider
} from "core"
import { SWRConfig } from 'swr'
import { Provider } from "@/ui/components/ui/provider"
import { MainPage } from './pages/MainPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { PublicShareViewer } from './pages/PublicShareViewer'
import { DownloadAppPage } from './pages/DownloadAppPage'
import { Toaster } from '@/ui/components/ui/toaster'

import "./i18n/config"
import useLocalizeDocumentAttributes from "@/i18n/useLocalizeDocumentAttributes"
import { useReaderLinkOrigin } from '@/hooks/useReaderLinkOrigin'
import { readAuthToken } from "@/utilities/authToken"

const AppContent = () => {
    return (
        <Suspense fallback={<div>Loading...</div>}>
            <Routes>
                {/* Public read-only shared chat — no auth, no circle bootstrap */}
                <Route path="aichat/share/:shareToken" element={<PublicShareViewer />} />
                {/* Public app download page (the share viewer's QR code target) */}
                <Route path="download-app" element={<DownloadAppPage />} />
                {/* New chat route */}
                <Route path="aichat/:circleId/new" element={
                    <CircleDataWrapper>
                        <MainPage />
                    </CircleDataWrapper>
                } />
                {/* Specific chat route */}
                <Route path="aichat/:circleId/chat/:chatId" element={
                    <CircleDataWrapper>
                        <MainPage />
                    </CircleDataWrapper>
                } />
                {/* Default route (redirects to /new) */}
                <Route path="aichat/:circleId" element={
                    <CircleDataWrapper>
                        <MainPage />
                    </CircleDataWrapper>
                } />
                <Route path="aichat/:circleId/reset-password" element={
                    <CircleDataWrapper>
                        <ResetPasswordPage />
                    </CircleDataWrapper>
                } />
            </Routes>
        </Suspense>
    )
}

const CircleDataWrapper = ({ children }: { children: React.ReactNode }) => {
    const { circleId } = useParams()
    const api = useApi()
    // Load the profile here (authed routes only) — NOT in AppContent — so the
    // public share route never fires an unauthenticated /ua/profile request.
    const { data: profile } = useProfile()

    // Make inline citation pills carry the origin params and open in a new tab,
    // matching the Sources cards.
    useReaderLinkOrigin(circleId)

    return (
        <LoadCircleData circleId={circleId}>
            <LanguageSelectionContextProvider>
                <CircleProvider profile={profile} api={api}>
                    <AuthProvider api={api}>
                        <ChatProvider>
                            <FilterProvider>
                                <AssistantsProvider api={api}>
                                    {children}
                                </AssistantsProvider>
                            </FilterProvider>
                        </ChatProvider>
                    </AuthProvider>
                </CircleProvider>
            </LanguageSelectionContextProvider>
        </LoadCircleData>
    )
}

export const App = () => {
    useLocalizeDocumentAttributes()

    const config = {
        root: isLocalhost() ? `http://localhost:3002/ua` : "/ua",
        // Authenticate by bearer token, not the cross-site iframe cookie.
        tokenAuth: true,
    }

    const api = new API(config)
    // Restore any persisted bearer token so the first profile fetch authenticates
    // by header instead of the (blocked) third-party cookie.
    const storedToken = readAuthToken()
    if (storedToken) api.setAuthToken(storedToken)

    return (
        <Provider>
            <HashRouter basename="">
                <APIProvider api={api}>
                    <SWRConfig value={{
                        onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
                            // Don't retry on auth errors (user not logged in)
                            const status = (error as any)?.status;
                            if (status === 401 || status === 403) return;

                            // Default retry with exponential backoff for other errors
                            if (retryCount >= 3) return;
                            setTimeout(() => revalidate({ retryCount }), 5000 * 2 ** retryCount);
                        }
                    }}>
                        <AppContent />
                    </SWRConfig>
                </APIProvider>
            </HashRouter>
            <Toaster />
        </Provider>
    )
}

export default App
